/**
 * What counts as profit — the TERMS, in one place.
 *
 * The arithmetic here is two subtractions, and that is not what this file is
 * for. The bug this prevents has never been a mistyped minus sign; it is a
 * disagreement about **which terms belong in the total**.
 *
 * It already happened once in this codebase. An audit on 2026-07-17 found one
 * screen reporting „წმინდა მოგება" with operating expenses deducted and
 * another reporting it without — two different numbers under the same words on
 * the same page, each internally consistent, and no way for the shopkeeper to
 * tell which was the real one. The fix at the time was to align the copies by
 * hand and leave a comment on each saying it must match the others. There were
 * four such copies (`kpi-cards`, `reports-tab`, `totals-cards`, `hero-card`),
 * and a comment is not a mechanism.
 *
 * So the terms are named here, once, and the doc is the specification:
 *
 *   gross profit = revenue − cost of goods sold
 *   net profit   = gross profit − operating expenses
 *
 * ⚠️ **PURCHASES ARE NOT A TERM.** Restocking is money out of the drawer, and
 * every screen shows it, but buying stock converts cash into inventory — it
 * does not consume it. Its cost reaches profit later, as COGS, on the day each
 * item sells. Subtracting purchases here would double-count them and make a
 * well-stocked shop look like a failing one.
 *
 * Revenue must already be NET of returns (`saleAmount`), and COGS must already
 * be signed the same way (`cogsOf`) — otherwise a refund adds to profit twice.
 */

/** Revenue less what the goods cost. The trading margin, before running costs. */
export function grossProfit(revenue: number, cogs: number): number {
  return revenue - cogs
}

/**
 * What the business actually kept: gross profit less rent, salaries,
 * utilities and the rest.
 */
export function netProfit(revenue: number, cogs: number, operatingExpenses: number): number {
  return grossProfit(revenue, cogs) - operatingExpenses
}

/**
 * Net profit as a percentage of revenue.
 *
 * Zero revenue returns null, not 0 and not Infinity: a shop that sold nothing
 * has no margin to report, and a screen showing „0%" says something false
 * about a day that simply has no answer yet.
 */
export function marginPct(netProfitValue: number, revenue: number): number | null {
  return revenue > 0 ? (netProfitValue / revenue) * 100 : null
}

/**
 * One product's margin: what its price keeps over its cost, as a FRACTION OF
 * THE PRICE (0.3 = 30%). The same definition as `marginPct` above, for one
 * unit instead of a period.
 *
 * It used to be typed out in four places — the margin-risk list, the purchase
 * dialog's cost warning, and twice in the waybill receive (retail and
 * wholesale). All four divided by the price; a fifth that divided by the COST
 * would call a 30% margin 43% and still look right (audit, 2026-09-23).
 *
 * No price → null: there is no margin to speak of, and 0 would be a claim.
 */
export function unitMargin(price: number, cost: number): number | null {
  return price > 0 ? (price - cost) / price : null
}

/**
 * The product form's hint, „მოსალოდნელი მოგება: ₾X · მარჟა Y%". Until 1.33 the
 * form divided by the PURCHASE price (a markup, ფასნამატი) while every other
 * screen showed `unitMargin` (÷ the sale price): ₾50 → ₾100 read 100 % there
 * and 50 % everywhere else. The owner chose მარჟა everywhere (2026-10-10).
 */
export function profitHint(sale: number, purchase: number): { profit: number; marginPct: number | null } {
  const m = unitMargin(sale, purchase)
  return { profit: sale - purchase, marginPct: m == null ? null : Math.round(m * 100) }
}

/**
 * The price that keeps a product's margin when its cost moves to `newCost`,
 * rounded UP to the tetri (a price rounded down would give the margin away one
 * tetri at a time). A margin that is none, negative, or 95% and over is not
 * kept — the suggestion falls back to the cost itself rather than to a price
 * twenty times it.
 */
export function priceKeepingMargin(newCost: number, margin: number | null): number {
  const ceil2 = (v: number) => Math.ceil(Math.round(v * 1e6) / 1e4) / 100
  return margin != null && margin > 0 && margin < 0.95 ? ceil2(newCost / (1 - margin)) : ceil2(newCost)
}

/**
 * „სულ დახარჯული" — the money that LEFT the business: purchases plus
 * operating expenses, LESS the write-offs among those expenses.
 *
 * A write-off (076) is an operating expense on purpose — profit must lose
 * what a broken bottle cost. But it is not money spent: the bottle's cost
 * left the drawer once, when it was bought, and sits in purchases already.
 * Adding the write-off on top counted the same lari twice (SAMGORI audit,
 * 2026-10-02: ₾622 of write-offs inflated their „spent").
 */
export function moneySpent(purchases: number, operatingExpenses: number, writeOffs: number): number {
  return purchases + operatingExpenses - writeOffs
}
